import { useToast, getErrorMessage } from '@/app/providers';

interface UseCrudNotificationsOptions {
  entityName: string; // "Unidad", "Usuario", "Amenidad"
  // Mensajes personalizados opcionales
  messages?: {
    createSuccess?: string;
    updateSuccess?: string;
    deleteSuccess?: string;
  };
}

interface UseCrudNotificationsReturn {
  // Wrappers que muestran toast automáticamente
  withCreateNotification: <T>(fn: () => Promise<T>) => Promise<T>;
  withUpdateNotification: <T>(fn: () => Promise<T>) => Promise<T>;
  withDeleteNotification: <T>(fn: () => Promise<T>) => Promise<T>;
  // Helper genérico
  withNotification: <T>(fn: () => Promise<T>, successMsg: string) => Promise<T>;
}

/**
 * Hook para mostrar notificaciones de éxito/error automáticamente después de operaciones CRUD.
 * Estandarizado para usar en todos los módulos admin.
 *
 * @example
 * const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
 *   entityName: 'Unidad',
 * });
 *
 * // En handleSave:
 * await withCreateNotification(() => unitRepo.createUnit(payload));
 *
 * // En handleDelete:
 * await withDeleteNotification(() => unitRepo.deleteUnit(id));
 */
export function useCrudNotifications(options: UseCrudNotificationsOptions): UseCrudNotificationsReturn {
  const toast = useToast();
  const { entityName, messages } = options;

  const withNotification = async <T>(fn: () => Promise<T>, successMsg: string): Promise<T> => {
    try {
      const result = await fn();
      toast.success(successMsg);
      return result;
    } catch (error) {
      toast.error(getErrorMessage(error));
      throw error; // Re-throw para que el caller pueda manejarlo
    }
  };

  return {
    withCreateNotification: (fn) =>
      withNotification(fn, messages?.createSuccess ?? `${entityName} creado correctamente`),

    withUpdateNotification: (fn) =>
      withNotification(fn, messages?.updateSuccess ?? `${entityName} actualizado correctamente`),

    withDeleteNotification: (fn) =>
      withNotification(fn, messages?.deleteSuccess ?? `${entityName} eliminado correctamente`),

    withNotification,
  };
}
